/**
 * toolkitPages.js — content for the 12 category toolkit pages at /ai-lab/:category.
 *
 * Each page renders five sections in this order:
 *   1. WHAT YOU CAN DO   → whatYouCanDo { intro, outcomes[], reality }
 *   2. HOW TO DO IT      → howToDoIt[]   (steps; optional taskId / toolId links)
 *   3. RECOMMENDED TOOLS → recommendedTools[] (exactly one primary: true)
 *   4. PROMPTS           → featuredTaskIds[] (components resolve via TASK_BY_ID)
 *   5. EXAMPLES          → examples[] (text-only before / after / result)
 * plus proTips[] and relatedCategories[].
 *
 * Only reference tool ids from aiTools.js and task ids from prompts/*.js.
 * Example brands are fictional. Plain ES module — no imports.
 */

export const TOOLKIT_PAGES = [
  // ───────────────────────────────────────────────────────────── PACKAGING ──
  {
    category: "packaging",
    title: "Packaging Design with AI",
    subtitle: "Go from a blank can to three shelf-ready directions, a render and a designer-ready brief in a day.",
    whatYouCanDo: {
      intro:
        "AI is strong at the front end of packaging: generating directions, naming concepts, building color and type systems, writing the image prompts and rendering mockups you can react to. It is also good at the paperwork — briefs, flavor-extension logic, back-panel copy. What you get is a way to make the big decisions before you pay a designer to execute them.",
      outcomes: [
        "Three named packaging directions with hex palettes and type pairings in an afternoon instead of a three-week agency round",
        "A photoreal render of your pack on a studio background you can show buyers and co-founders",
        "A flavor-extension system that keeps the line looking like one family as you add SKUs",
        "A shelf mockup with your pack next to the competitive set to test the 3-second read",
        "An agency-ready creative brief so the designer you hire starts from your decisions, not a blank page",
        "A buyer's-eye audit of your current packaging with a scored rubric",
        "Front-panel hierarchy, romance copy in three lengths and a claims table",
        "A multipack or club-pack plan before you commit to a carton structure",
      ],
      reality:
        "AI cannot produce print-ready dielines, Pantone-matched artwork or a compliant nutrition and ingredient panel. Use it to decide what you want, then hand a brief, references and hex codes to a packaging designer for production files — and have a regulatory consultant check the panel before it goes to the printer.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Start with your Brand Profile",
        detail: "Fill in positioning, target consumer, claims, competitors, style words and what to avoid. Every packaging prompt pulls from it, so ten minutes here saves an hour of re-prompting.",
      },
      {
        step: 2,
        title: "Generate three concepts in ChatGPT",
        detail: "Run Create Packaging Concept. Read all three, then pick one direction. Do not try to blend them — a blended concept is a weak concept.",
        taskId: "packaging-concept",
        toolId: "chatgpt",
      },
      {
        step: 3,
        title: "Render the direction you picked",
        detail: "Copy the concept's image prompt (item #10) into ChatGPT Image Generation or Midjourney, or run Create Product Render for more control. Generate four to six takes and keep the best one.",
        taskId: "packaging-product-render",
        toolId: "chatgpt-images",
      },
      {
        step: 4,
        title: "Extend to flavors and put it on a shelf",
        detail: "Run Create Flavor Extension so the line reads as a family, then Create Shelf Display Mockup with competitor packs beside it. If your pack disappears at arm's length, go back to step 2.",
        taskId: "packaging-flavor-extension",
      },
      {
        step: 5,
        title: "Write the creative brief",
        detail: "Run Create Packaging Creative Brief and attach your best render, hex codes and references. This is the document that makes a designer quote you a fixed price.",
        taskId: "packaging-creative-brief",
      },
      {
        step: 6,
        title: "Hand off for dielines and print files",
        detail: "A packaging designer builds the dieline, sets type properly and preps print-ready artwork in Illustrator. Get the regulatory panel reviewed before the first print run.",
        toolId: "illustrator",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Concepts, briefs, flavor systems and label copy — and it renders the mockups in the same chat.",
        whatToType: "Paste the Packaging Concept prompt. Then: “Render concept 2 as a 16 oz can on a white studio background.”",
      },
      {
        toolId: "chatgpt-images",
        useFor: "Product renders and shelf mockups where the brand name on the pack needs to be legible.",
        whatToType: "Upload your current label art or render, then paste the Product Render prompt.",
      },
      {
        toolId: "midjourney",
        useFor: "Mood boards, materials and finishes before you commit to a direction.",
        whatToType: "“matte black stand-up pouch, copper foil wordmark, soft studio light --ar 4:5”",
      },
      {
        toolId: "illustrator",
        useFor: "Dielines, Pantone matching and print-ready artwork — the designer's step, not the AI's.",
        whatToType: "Send your designer the brief, your best render and the hex codes.",
      },
      {
        toolId: "canva",
        useFor: "A one-page visual brief and quick side-by-side comps to share with your designer.",
        whatToType: "Drop your three renders on one page with the hex codes under each.",
      },
    ],
    featuredTaskIds: [
      "packaging-concept",
      "packaging-product-render",
      "packaging-flavor-extension",
      "packaging-shelf-display",
      "packaging-creative-brief",
    ],
    examples: [
      {
        title: "Before → After: the concept prompt",
        before: "“Design packaging for my oat bar.”",
        after:
          "The Prompt Builder version for Ember Oat Bars names the consumer (busy parents 30–45 shopping at Target), the positioning (warm, real-food energy — not a sports bar), the competitive set, the format (flow-wrap bar plus 5-count carton), three style words and what to avoid (the brown-kraft health-food look). It asks for three named concepts, each with a hex palette, front-panel hierarchy, flavor system and an image prompt.",
        result:
          "Three directions came back — “Hearth” (deep ember red, cream serif), “Morning Light” (warm gradient, friendly sans) and “Field Notes” (illustrated oats, muted green). Hearth's image prompt rendered a convincing 5-count carton on the first try.",
      },
      {
        title: "Finished output: a flavor extension",
        before: "Tidewater Sparkling Tea had one peach can and needed three more flavors that still looked like one brand.",
        after:
          "The Flavor Extension prompt listed the existing palette and type, asked for hex assignments that stay in the same family, a rule for where the flavor cue sits, and a four-can lineup image prompt.",
        result:
          "Yuzu (#F2C14E), Hibiscus (#C84B6B) and Mint (#5FAF8F), each with a matching ingredient illustration rule, plus a lineup render that looked like a real planogram. The rule went straight into the brand guidelines.",
      },
      {
        title: "Finished output: a shelf test on current packaging",
        before: "Northpeak Jerky uploaded a photo of its current bag with “Is this good?”",
        after:
          "Evaluate My Packaging ran the 3-second shelf test and the 10-criterion rubric against the named competitive set and the convenience channel.",
        result:
          "Scored 5/10 on shelf read. It flagged that “Original” was set larger than the brand name, recommended moving the brand to the top third and adding a protein callout. Both notes went directly into the redesign brief.",
      },
      {
        title: "Before → After: the designer brief",
        before: "“Here's my idea — can you make it?” plus three screenshots.",
        after:
          "The Creative Brief prompt produced objectives, mandatories (UPC, net weight, allergen statement placement), hierarchy, references, three flavors, carton and deliverable file formats.",
        result:
          "The designer quoted a fixed price on the spot because scope was clear, and the first round matched the chosen concept instead of starting over.",
      },
    ],
    proTips: [
      "Generate every render four to six times and pick. AI images vary a lot per take — never judge a concept on one image.",
      "Keep on-pack text in image prompts short: brand name plus one descriptor. Long text garbles.",
      "Ask to see the concept as a 12-pack carton and as a shelf of eight facings before choosing. A design that works on one can may fail as a block.",
      "Once you decide, save the hex codes and typeface names in your Brand Profile. Social, website and ads all pull from them.",
    ],
    relatedCategories: ["branding", "photography", "operations"],
  },

  // ────────────────────────────────────────────────────────────── BRANDING ──
  {
    category: "branding",
    title: "Logo & Brand Identity with AI",
    subtitle: "Name it, give it a face, and make sure you can actually own it — before you print a single label.",
    whatYouCanDo: {
      intro:
        "AI is excellent at breadth: 25 names in five styles, three logo territories and a full identity system with palette and type in about an hour. It is the fastest way to see real options and make decisions. The craft steps — vectorizing, kerning, trademark clearance — still need a tool or a human.",
      outcomes: [
        "25 name candidates with rationale, pronunciation and a preliminary trademark-risk note",
        "Three logo territories (wordmark, monogram, emblem) with typography, color and lockups",
        "Rendered logo concepts you can react to the same day",
        "A full identity system: personality, archetype, palette with hex codes, type, photography style",
        "Your brand story in 50, 100 and 300 words plus 15 taglines",
        "A voice guide so every caption, email and product page sounds like the same brand",
        "Clean SVG and PDF logo files your printer and co-packer will accept",
        "A shortlist checked against the USPTO database and domain availability before you fall in love",
      ],
      reality:
        "AI cannot clear a trademark, and it will happily generate a name that is already registered in your class. Run a USPTO search and a domain and handle check yourself, then have a trademark attorney do a proper clearance before you invest in packaging. AI logos also come out as pixels — they must be converted to vector and cleaned up before print.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Generate names, then shortlist five",
        detail: "Run Generate Brand Names. Read the rationale, say each one out loud, and keep five. Ignore any you already love — you are looking for the one you can own.",
        taskId: "branding-name-generator",
        toolId: "chatgpt",
      },
      {
        step: 2,
        title: "Check before you commit",
        detail: "For each name: search the USPTO trademark database in your class (29, 30 or 32 for most food and beverage), check the .com and the Instagram and TikTok handles, and Google the name plus your category. Kill anything with a live conflict. Have an attorney clear the finalist.",
      },
      {
        step: 3,
        title: "Pick a logo territory",
        detail: "Run Create Logo Direction. Choose wordmark, monogram or emblem based on where the logo must live — a 12 oz can, a favicon, an embroidered hat.",
        taskId: "branding-logo-direction",
      },
      {
        step: 4,
        title: "Render the mark in Ideogram",
        detail: "Run Create Logo Concept Image and paste the prompt into Ideogram or Recraft — both handle lettering far better than general image models. Generate 8–12 and keep two or three.",
        taskId: "branding-logo-image-prompt",
        toolId: "ideogram",
      },
      {
        step: 5,
        title: "Convert to vector",
        detail: "Run Turn AI Logo Into Usable Vector Artwork, upload the PNG to Vectorizer.AI, then clean paths in Illustrator. Export SVG, PDF and PNG, in full color, black and white.",
        taskId: "branding-vectorize-logo",
        toolId: "vectorizer",
      },
      {
        step: 6,
        title: "Build the system around it",
        detail: "Run Create Brand Identity System, then Create Brand Voice Guide. Save the palette, type and voice pillars to your Brand Profile so every other page stays on-brand.",
        taskId: "branding-identity-system",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Names, logo direction, identity system and story — the thinking before the drawing.",
        whatToType: "Paste Generate Brand Names. Then: “Shortlist the five with the lowest trademark risk and tell me why.”",
      },
      {
        toolId: "ideogram",
        useFor: "Rendering wordmarks and logo concepts with text that is actually spelled right.",
        whatToType: "“Minimal wordmark logo for MOSSBROOK, kombucha, humanist serif, forest green on cream, flat vector style”",
      },
      {
        toolId: "recraft",
        useFor: "Vector-native logo output and icon systems that stay consistent.",
        whatToType: "Choose the Vector style, then paste the Logo Concept Image prompt.",
      },
      {
        toolId: "vectorizer",
        useFor: "Turning your chosen PNG into an SVG in about a minute.",
        whatToType: "Upload the logo PNG, download the SVG, open it in Illustrator or Figma.",
      },
      {
        toolId: "illustrator",
        useFor: "Cleaning paths, fixing letterforms and exporting the final file set.",
        whatToType: "Open the SVG, merge overlapping shapes, export SVG, PDF and PNG at 3 sizes.",
      },
    ],
    featuredTaskIds: [
      "branding-name-generator",
      "branding-logo-direction",
      "branding-logo-image-prompt",
      "branding-vectorize-logo",
      "branding-identity-system",
    ],
    examples: [
      {
        title: "Before → After: the naming prompt",
        before: "“Give me a name for my kombucha.”",
        after:
          "The Prompt Builder version specifies the category, the consumer, the positioning (woodland, low-sugar, Pacific Northwest), tone words, names to avoid (anything with “booch”) and asks for 25 names across five styles, each with a trademark-risk note and domain suggestions.",
        result:
          "The founder shortlisted “Mossbrook,” “Fernline” and “Understory.” A USPTO search killed Understory (live mark in Class 32). Mossbrook cleared the preliminary check and the .com was available for $12. Mossbrook Kombucha was born.",
      },
      {
        title: "Before → After: the logo prompt",
        before: "“Make me a logo.”",
        after:
          "Create Logo Direction took the name, positioning, a type preference and the three places the logo must work (12 oz can, 32 px favicon, embroidered hat). It returned wordmark, monogram and emblem territories with lockups and an image prompt for each.",
        result:
          "Mossbrook chose the emblem — a fern inside a circle over a stacked wordmark. Ideogram rendered 12 variants from the prompt; two were usable, one was chosen.",
      },
      {
        title: "Finished output: vector conversion",
        before: "Northpeak Jerky's founder had a 1024 px PNG logo from ChatGPT and a printer asking for “vector files.”",
        after:
          "The vectorize walkthrough gave him the exact file list (SVG, PDF, EPS; full color, black, white; CMYK for print) and the steps. Vectorizer.AI produced the SVG; 20 minutes in Illustrator merged overlapping paths and fixed a wobbly “k.”",
        result: "The co-packer's label printer accepted the PDF on first submission. No “please send vector files” email.",
      },
      {
        title: "Finished output: a voice guide",
        before: "Halcyon Electrolyte had two freelancers writing captions that sounded like two different brands.",
        after:
          "Create Brand Voice Guide produced tone pillars (“calm, exact, a little dry”), do-and-don't vocabulary (say “hydrate,” never “crush”), grammar rules and before/after rewrites of five real captions.",
        result: "A month later nobody could tell which freelancer wrote which post.",
      },
    ],
    proTips: [
      "Search the trademark before you buy the domain, and buy the domain before you tell anyone the name.",
      "Ask for the logo in black on white first. If it doesn't work in one color, it won't work on a can.",
      "Use ChatGPT for the thinking and Ideogram or Recraft for the mark — general image models misspell words.",
      "Show three finalists to people who've never heard of your brand and ask what they think it sells.",
    ],
    relatedCategories: ["packaging", "website", "social"],
  },

  // ─────────────────────────────────────────────────────────── PHOTOGRAPHY ──
  {
    category: "photography",
    title: "AI Product Photography",
    subtitle: "Studio shots, lifestyle scenes and a full Amazon image set from one clean phone photo of your product.",
    whatYouCanDo: {
      intro:
        "Upload a decent photo of your real package and AI can put it on a marble counter, in a gym bag, on a picnic table at golden hour or on pure white for a listing. The output is good enough for social, website, ads and buyer decks, and a seven-image e-commerce set takes an afternoon instead of a shoot day.",
      outcomes: [
        "A studio hero shot on white, marble, wood or colored seamless — no photographer, no light tent",
        "Lifestyle scenes matched to your consumer and occasion: a trail break, a desk at 3 pm, a kid's lunchbox",
        "Macro ingredient imagery for pack backs, the website and Amazon A+ modules",
        "A complete 7-image Amazon or Shopify set plan: main, infographic, lifestyle, comparison, size, ingredients, reviews",
        "Retailer-specific imagery — your product on a natural-grocery shelf or a mass end cap — for a buyer deck",
        "Six on-brand social images with overlay copy and captions",
        "A dim phone photo turned into something that looks shot in a studio",
      ],
      reality:
        "AI still mangles small label text and will quietly change your package shape if you let it. For an Amazon main image — pure white background, product filling 85% of the frame, no props, no text, showing the actual product — shoot the real thing and use remove.bg; a render that looks fake can get the listing suppressed. For the hero shot that goes on a billboard or in a national line review, a real product photographer is still worth the money.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Take one good reference photo",
        detail: "Daylight, white background, label square to the camera, no glare. Run it through remove.bg for a clean cut-out. This one file powers everything below.",
        toolId: "removebg",
      },
      {
        step: 2,
        title: "Make the studio shot",
        detail: "Upload the cut-out to ChatGPT and paste Create Studio Product Shot. Add “keep the label, shape and proportions exactly as uploaded.” Generate four to six and zoom to 100% on each.",
        taskId: "photography-studio-shot",
        toolId: "chatgpt-images",
      },
      {
        step: 3,
        title: "Build the lifestyle scene",
        detail: "Run Create Lifestyle Photography with one occasion pulled from your target consumer. Use Midjourney for mood and ChatGPT when the label must stay legible.",
        taskId: "photography-lifestyle",
      },
      {
        step: 4,
        title: "Plan the e-commerce set",
        detail: "Run Create E-commerce Image Set. Shoot Image 1 for real to meet Amazon's main-image rules; use the prompts for images 2–7 and add overlay copy in Canva on a 2000 × 2000 px canvas.",
        taskId: "photography-ecommerce-set",
        toolId: "chatgpt",
      },
      {
        step: 5,
        title: "Check and retouch",
        detail: "Zoom in on every image: label spelling, cap shape, ingredient count, reflections. Fix in Photoshop or regenerate. Export at 4:5 and 1:1 so Instagram and Amazon each get their crop.",
        toolId: "photoshop",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt-images",
        primary: true,
        useFor: "Keeping your actual package intact while changing the scene, surface and light.",
        whatToType: "Upload your cut-out photo, then paste the Studio Product Shot prompt.",
      },
      {
        toolId: "midjourney",
        useFor: "Lifestyle mood and ingredient textures where exact label fidelity matters less.",
        whatToType: "“overhead flat lay, granola in a ceramic bowl, linen, morning window light, 50mm --ar 4:5”",
      },
      {
        toolId: "removebg",
        useFor: "Cut-outs for AI scenes and the pure-white Amazon main image.",
        whatToType: "Upload the phone photo, download the transparent PNG.",
      },
      {
        toolId: "photoshop",
        useFor: "Spot fixes, compositing and checking the white background is true 255/255/255.",
        whatToType: "Open the render, fix the cap with Generative Fill, check the background with the eyedropper.",
      },
      {
        toolId: "firefly",
        useFor: "Commercially safe background extension and retouching for Adobe users.",
        whatToType: "Upload the studio shot, use Generative Expand to widen it to 16:9.",
      },
    ],
    featuredTaskIds: [
      "photography-studio-shot",
      "photography-lifestyle",
      "photography-ecommerce-set",
      "photography-edit-photo",
      "photography-ingredient",
    ],
    examples: [
      {
        title: "Before → After: the studio shot prompt",
        before: "“Make a nice photo of my hot sauce.”",
        after:
          "The Prompt Builder version for Solstice Hot Sauce uploads the 5 oz woozy bottle cut-out, locks the label, asks for a dark slate surface, a single hard key light from the left, a 45° angle, a few dried chiles as props and a 4:5 crop.",
        result:
          "Four images came back. Two had warped caps and were discarded. The other two became the website hero and an Instagram post the same day.",
      },
      {
        title: "Finished output: an Amazon image set",
        before: "Larkfield Granola had one photo on its listing and a 0.3% click-through rate.",
        after:
          "Image 1 was shot on a white sheet by a window and cut out with remove.bg. Images 2–7 came from the E-commerce Image Set prompts: a four-callout infographic, a bowl lifestyle shot, a sugar comparison versus the category, a hand for scale, an ingredient macro and a review quote card.",
        result:
          "The listing went live with seven images. Image 1 passed Amazon's review without a flag, and the founder reported click-through roughly doubling over the following month.",
      },
      {
        title: "Before → After: the lifestyle prompt",
        before: "“Product in a gym.”",
        after:
          "For Halcyon Electrolyte: the 16 oz bottle in a woman's hand on a trail bench at sunrise, Pacific Northwest, dew on the cap, shallow depth of field, label facing camera, no text overlay.",
        result: "Midjourney set the mood, ChatGPT composited the real bottle, and the image became the cover of the retailer deck.",
      },
      {
        title: "Finished output: a phone photo rescued",
        before: "Copperline Coffee's founder had one dim kitchen-counter photo of a 12 oz bag and no budget for a shoot.",
        after:
          "Edit My Product Photo asked ChatGPT to remove the background, correct the yellow color cast, place the bag on a warm oak surface with soft window light, and preserve the bag's crinkles and label exactly.",
        result: "A website-ready image in ten minutes. No reshoot.",
      },
    ],
    proTips: [
      "Upload the cut-out, not the raw photo. AI keeps your package far more faithfully when there's no background to confuse it.",
      "Write “do not change the label, shape, cap or proportions” in every prompt. Then check anyway.",
      "Amazon main image rules: pure white background, product at 85% or more of the frame, no props, no text, and it must show the real product. Use AI for images 2–7, not image 1.",
      "Generate at 4:5 and 1:1 from the start. Cropping later loses the edges you wanted.",
    ],
    relatedCategories: ["packaging", "social", "advertising"],
  },

  // ─────────────────────────────────────────────────────────── ADVERTISING ──
  {
    category: "advertising",
    title: "AI Advertising Creator",
    subtitle: "Write, visualize and test paid ads the way a performance team does — without the team.",
    whatYouCanDo: {
      intro:
        "Paid social is a volume game: many hooks, many headlines, many angles and the discipline to test them. AI is built for volume. It writes five primary texts and five headlines in seconds, scripts a UGC ad beat by beat, builds a campaign platform and renders a static ad with your headline on it. It will also tear down an ad that flopped and tell you why.",
      outcomes: [
        "A complete Meta ad set: 5 primary texts, 5 headlines, 3 descriptions, CTA and launch pairings",
        "10 headlines and 10 video hooks sorted by angle, so you test reasons to buy instead of word choices",
        "A word-for-word UGC script with timing, for a creator or for your own phone",
        "Five native-feeling TikTok ad concepts with 2-second hooks",
        "A full-funnel launch set: teaser, launch day, consideration and retargeting",
        "Static ad visuals with your headline rendered in 1:1, 4:5 and 9:16",
        "A scored teardown of an underperforming ad with the three fixes that matter",
        "A campaign platform with creative territories, channel split and messaging matrix",
      ],
      reality:
        "AI doesn't know your cost per purchase, your audience or what worked last month — feed it results and it will write against them. It will also invent claims (“clinically proven,” “#1 selling”) that get ads rejected or get you a letter; cut anything you can't substantiate. Media buying, pixel setup and budget pacing are still on you or your agency.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Lock the angles first",
        detail: "Run Create 10 Headlines & 10 Hooks. Pick three distinct angles to test — for example benefit, social proof and contrarian. Words come later.",
        taskId: "advertising-headlines",
        toolId: "chatgpt",
      },
      {
        step: 2,
        title: "Write one ad set per angle",
        detail: "Run Create Meta Ad once per angle. Strike any line with a claim that isn't on your label or backed by a source you could show a lawyer.",
        taskId: "advertising-meta-ad",
      },
      {
        step: 3,
        title: "Make the visual",
        detail: "For statics, run Create Static Ad Visual with your product cut-out uploaded. For video, run Create UGC Script and film it on a phone or send it to a creator.",
        taskId: "advertising-static-ad-image",
        toolId: "chatgpt-images",
      },
      {
        step: 4,
        title: "Size and finish in Canva",
        detail: "Export 1:1, 4:5 and 9:16. Keep the on-image headline under six words and the logo small. Leave the top and bottom 15% clear for platform UI.",
        toolId: "canva",
      },
      {
        step: 5,
        title: "Test, then feed the results back",
        detail: "Launch three angles × two visuals at a small daily budget for five to seven days. Paste the numbers into Analyze Existing Ad for the winner and the loser, then write round two against what you learned.",
        taskId: "advertising-analyze-ad",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Ad copy, hooks, UGC scripts and quick variants — and the static visual in the same chat.",
        whatToType: "Paste the Meta Ad prompt. Then: “Rewrite set 3 for a cold audience that has never heard of us.”",
      },
      {
        toolId: "claude",
        useFor: "Campaign platforms, ad teardowns and longer strategic writing.",
        whatToType: "Paste Create Campaign Concept and your last three months of results.",
      },
      {
        toolId: "chatgpt-images",
        useFor: "Static ad visuals with a legible headline and your real product.",
        whatToType: "Upload the product cut-out, then paste the Static Ad Visual prompt.",
      },
      {
        toolId: "canva",
        useFor: "Resizing to every placement, text overlays and export.",
        whatToType: "Open the AI image, apply the 4:5 template, duplicate to 1:1 and 9:16.",
      },
      {
        toolId: "higgsfield",
        useFor: "Short, punchy ad clips with dramatic camera moves from a single still.",
        whatToType: "Upload your hero image, pick a camera preset, add the 2-second hook as on-screen text.",
      },
    ],
    featuredTaskIds: [
      "advertising-meta-ad",
      "advertising-headlines",
      "advertising-ugc-script",
      "advertising-static-ad-image",
      "advertising-analyze-ad",
    ],
    examples: [
      {
        title: "Before → After: the Meta ad prompt",
        before: "“Write an Instagram ad for my jerky.”",
        after:
          "The Prompt Builder version for Northpeak Jerky names the consumer (men 25–45 who hike and hunt and already buy jerky at gas stations), the problem (sugary, rubbery), a real proof point from the label (6 g sugar per bag versus a 14 g category average), the offer (3-bag sampler, $19), tone (dry, outdoorsy, no bro) and asks for five primary texts across angles plus five headlines under 40 characters.",
        result:
          "“Jerky that isn't candy.” with the 6 g vs 14 g primary text won the first test at a 2.1% click-through rate. The founder-story angle lost and was cut.",
      },
      {
        title: "Finished output: a UGC script",
        before: "Cinder Cold Brew had a polished product video that wasn't converting.",
        after:
          "Create UGC Script produced a 30-second script: hook “I stopped buying $7 cold brew” (0–2 s), the problem, a pour shot, proof (20 servings per bottle with the math on screen) and a CTA with a code.",
        result: "The founder filmed it in her kitchen in one take. It beat the polished video three to one on cost per purchase.",
      },
      {
        title: "Before → After: the static ad prompt",
        before: "“Make an ad image.”",
        after:
          "For Ember Oat Bars: 4:5, the bar broken in half showing oats and chocolate, warm kitchen light, the headline “Breakfast that doesn't crash.” in the brand's cream type, logo bottom-right, 20% empty space top-left for platform UI.",
        result: "ChatGPT rendered the headline legibly on the second try. Canva resized it to 1:1 and 9:16 in two minutes.",
      },
      {
        title: "Finished output: a teardown",
        before: "Tidewater Sparkling Tea pasted its worst ad — 0.4% click-through — and asked what went wrong.",
        after:
          "Analyze Existing Ad scored it 3/10 on hook (the first line was the brand name) and 4/10 on proof. Top three fixes: lead with the benefit, replace the lifestyle shot with product-in-hand, add the price.",
        result: "The rewrite doubled click-through at the same spend.",
      },
    ],
    proTips: [
      "Test angles before words. Three different reasons to buy beat ten rewrites of the same reason.",
      "Every claim in an AI ad needs a source you could show a lawyer. “Zero sugar” is on your label; “healthiest” is not.",
      "Feed it your results. “This ad got 0.6% CTR and $41 CPA; this one got 1.8% and $19” turns the next round from guessing into iterating.",
      "Keep the on-image headline under six words and let the primary text do the explaining.",
    ],
    relatedCategories: ["social", "video", "photography"],
  },

  // ───────────────────────────────────────────────────────────────── VIDEO ──
  {
    category: "video",
    title: "AI Video Creator",
    subtitle: "Scripts, storyboards, product animation and a presenter on camera — a launch video without a crew.",
    whatYouCanDo: {
      intro:
        "AI can write the script, storyboard it with an image prompt per frame, animate your product render into a 5–10 second cinematic clip, voice it and put a presenter on camera who never flubs a line. Stitched together in CapCut, that is a real launch video. The pieces live in separate tools, so the workflow matters more here than anywhere else.",
      outcomes: [
        "A two-column script (visual | audio) timed to 15, 30, 60 or 90 seconds",
        "An 8–12 frame storyboard with an image prompt for every frame",
        "A 5–10 second product animation from a still render — slow orbit, condensation, pour",
        "Lifestyle clips with native sound generated from a text prompt",
        "An ElevenLabs-ready voiceover with pause markup and delivery notes",
        "A HeyGen presenter video of you or an avatar for buyer pitches and explainers",
        "A master video cut down to 9:16, 1:1, 16:9, a 15-second version and a 6-second bumper",
        "Ten UGC video concepts ranked so you know which to film first",
      ],
      reality:
        "AI video still struggles with hands, pouring liquids, legible label text and anything longer than about ten seconds in one shot. Expect to generate four to six takes per clip and keep one. Scenes with real people and anything a national buyer will watch on a big screen still benefit from a real shoot — AI is best for product-only shots, b-roll and presenter explainers.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Write the script",
        detail: "Run Write Video Script. Thirty seconds for ads, 60–90 for a launch or retailer video. Read it out loud with a timer before you do anything else.",
        taskId: "video-script",
        toolId: "chatgpt",
      },
      {
        step: 2,
        title: "Storyboard and render the stills",
        detail: "Run Create Storyboard, then generate each frame's still in ChatGPT Images or Midjourney. Every product frame should start from your real render or cut-out, never from a text description.",
        taskId: "video-storyboard",
      },
      {
        step: 3,
        title: "Animate the product and the b-roll",
        detail: "Run Create Product Animation, upload the still to Runway or Kling and paste the motion prompt. For lifestyle b-roll run Create Lifestyle Video Clip in Veo. Four to six takes each.",
        taskId: "video-product-animation",
        toolId: "runway",
      },
      {
        step: 4,
        title: "Add the voice or a presenter",
        detail: "Run Write Voiceover Script and generate it in ElevenLabs, or run Create AI Presenter Script and record it in HeyGen with an avatar or your own clone.",
        taskId: "video-voiceover",
        toolId: "elevenlabs",
      },
      {
        step: 5,
        title: "Assemble and cut down",
        detail: "In CapCut: clips, voiceover, captions, music, logo end card. Then run Create Cut-Downs & Variations to plan 9:16, 1:1, the 15-second cut and the bumper.",
        taskId: "video-resize-variations",
        toolId: "capcut",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Scripts, storyboards, shot lists and the storyboard stills.",
        whatToType: "Paste Write Video Script. Then: “Now storyboard this in 10 frames with an image prompt per frame.”",
      },
      {
        toolId: "runway",
        useFor: "Image-to-video product animation from your render.",
        whatToType: "Upload the product render, paste the Product Animation motion prompt, set 5 seconds.",
      },
      {
        toolId: "veo",
        useFor: "Text-to-video lifestyle clips with sound baked in.",
        whatToType: "Paste the Lifestyle Video Clip prompt and ask for 8 seconds at 16:9.",
      },
      {
        toolId: "heygen",
        useFor: "Founder or presenter videos for buyers, explainers and multi-language versions.",
        whatToType: "Paste the AI Presenter Script into the script box, pick an avatar or your own clone.",
      },
      {
        toolId: "capcut",
        useFor: "Editing, captions, music and resizing to every platform.",
        whatToType: "Import clips and the voiceover file, auto-captions on, export 9:16 and 1:1.",
      },
    ],
    featuredTaskIds: [
      "video-script",
      "video-storyboard",
      "video-product-animation",
      "video-ai-presenter",
      "video-resize-variations",
    ],
    examples: [
      {
        title: "Before → After: the animation prompt",
        before: "“Make a video of my can spinning.”",
        after:
          "For Tidewater Sparkling Tea: a clean 12 oz can render uploaded; the motion prompt asks for a slow 180° orbit, condensation beads forming, soft top light, dark teal background, a 10% camera push-in, label kept sharp, five seconds, no text.",
        result: "Runway gave five takes. Take three kept the label crisp all the way through and became the first five seconds of every ad.",
      },
      {
        title: "Finished output: a 45-second launch video",
        before: "Ember Oat Bars needed a launch video for the website and a line review with a budget under $100.",
        after:
          "Script, a 10-frame storyboard, six stills rendered, three animated in Runway, founder voiceover cloned in ElevenLabs from a two-minute sample, assembled in CapCut with captions.",
        result: "Under $60 and two evenings. It runs as the website hero and opened the retail line review.",
      },
      {
        title: "Before → After: the presenter video",
        before: "“Record me pitching.”",
        after:
          "Create AI Presenter Script produced a 90-second buyer video segmented into intro, why now, product, velocity and the ask, with on-screen text per segment and pause markup. Recorded in HeyGen with the founder's avatar.",
        result: "Northpeak Jerky emailed it to 12 regional buyers. Four replied; two meetings.",
      },
      {
        title: "Finished output: cut-downs from one master",
        before: "One 60-second master and four platforms that each want a different shape.",
        after:
          "Create Cut-Downs & Variations planned a 9:16 with captions in the upper safe zone, a 1:1 with the can centered, a 15-second hook-first cut and a 6-second bumper of the orbit plus logo.",
        result: "Four placements from one edit session.",
      },
    ],
    proTips: [
      "Start every product clip from your real render or cut-out. Text-to-video invents a different package every time.",
      "Keep clips at five to eight seconds and cut between them. That's how real ads are built anyway.",
      "Generate the voiceover first, then time the edit to it. Editing to silence and adding voice later never fits.",
      "Captions on by default. Most of social watches muted.",
    ],
    relatedCategories: ["advertising", "social", "photography"],
  },

  // ──────────────────────────────────────────────────────────────── SOCIAL ──
  {
    category: "social",
    title: "AI Social Media Creator",
    subtitle: "A month of on-brand posts, hooks and Reels concepts planned in an afternoon, not a Sunday night.",
    whatYouCanDo: {
      intro:
        "Social is a consistency problem, and AI fixes the part that kills founders: deciding what to post. It defines your pillars, fills a 30-day calendar, writes captions in four lengths, drafts 20 hooks and briefs a creator with the FTC lines included. You still shoot, post and reply — but from a plan.",
      outcomes: [
        "Four or five content pillars with ratios and hook templates",
        "A 30-day calendar with pillar, format, hook, CTA and the asset you need each day",
        "Ten captions for one topic across micro, short, medium and long",
        "20 hooks with spoken line, on-screen text and first-frame visual",
        "Ten Reels / TikTok concepts built on native formats",
        "A complete creator brief with deliverables, do/don'ts and FTC disclosure rules",
        "A comment and DM reply playbook in your voice",
        "A bio and highlight structure with character counts",
      ],
      reality:
        "AI doesn't know what's trending on your feed this week, and a caption with no real photo or clip behind it is just a caption. It also can't build community — replies, DMs and showing up on camera as the founder are yours. Treat the calendar as a plan you break when something real happens.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Define your pillars",
        detail: "Run Define Content Pillars. Four or five, and one should be founder or behind-the-scenes — it's the content only you can make.",
        taskId: "social-content-pillars",
        toolId: "claude",
      },
      {
        step: 2,
        title: "Fill the 30-day calendar",
        detail: "Run Create 30-Day Content Calendar with your pillars pasted in. Ask it to mark which days need a photo, a clip or a graphic so you can batch the shoot.",
        taskId: "social-30-day-calendar",
        toolId: "chatgpt",
      },
      {
        step: 3,
        title: "Batch the assets",
        detail: "One shoot day covers two weeks. Fill gaps with Photography AI's Create Social Content Images and make graphics in Canva. Never write the caption before you have the asset.",
        toolId: "canva",
      },
      {
        step: 4,
        title: "Write captions and hooks in batches",
        detail: "Run Write 10 Captions per topic and Write 20 Hooks for Reels. Feed the photo or clip to ChatGPT and say “caption this for pillar X.”",
        taskId: "social-hooks",
      },
      {
        step: 5,
        title: "Schedule, reply, review",
        detail: "Run Write Comment & DM Replies so you can answer in 30 seconds, in voice. Every two weeks, paste your top five posts back in and regenerate the next calendar.",
        taskId: "social-community-replies",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Calendars, captions, hooks and Reels concepts — and captioning a photo you upload.",
        whatToType: "Paste the 30-Day Content Calendar prompt. Then: “Turn days 1–7 into full captions.”",
      },
      {
        toolId: "claude",
        useFor: "Pillars, voice consistency and influencer briefs.",
        whatToType: "Paste Define Content Pillars inside a Project that holds your Brand Profile.",
      },
      {
        toolId: "canva",
        useFor: "Graphics, carousels, resizing and scheduling from its content planner.",
        whatToType: "Open a 4:5 template, drop in the AI image, add the hook as overlay text.",
      },
      {
        toolId: "capcut",
        useFor: "Reels editing, auto-captions and trending templates.",
        whatToType: "Import the clip, auto-captions, add the hook as the first-frame text.",
      },
      {
        toolId: "chatgpt-images",
        useFor: "Filling image gaps on days you have no photo.",
        whatToType: "Upload your product cut-out and paste a Create Social Content Images prompt.",
      },
    ],
    featuredTaskIds: [
      "social-30-day-calendar",
      "social-content-pillars",
      "social-hooks",
      "social-reels-concepts",
      "social-influencer-brief",
    ],
    examples: [
      {
        title: "Before → After: the calendar prompt",
        before: "“Give me 30 posts for my kombucha.”",
        after:
          "The Prompt Builder version for Mossbrook Kombucha lists four pillars (Brew Room, Low-Sugar Truths, Forest Floor ingredient stories, Founder Fridays), five posts a week on Instagram and TikTok, a Sprouts launch on day 18 and two available shoot days.",
        result:
          "A 30-day table with 12 Reels, 10 photos and 8 carousels, a launch cluster around day 18, and an asset column that let the founder shoot everything in two days.",
      },
      {
        title: "Finished output: 20 hooks",
        before: "Halcyon Electrolyte kept opening Reels with the brand name and losing viewers in the first second.",
        after:
          "Write 20 Hooks returned lines by category: “The electrolyte math nobody shows you” (curiosity), “Stop drinking this after your run” (contrarian), “POV: it's 3 pm and you've had zero water” (relatable) — each with on-screen text and a first-frame visual.",
        result: "Five were filmed in one afternoon. The 3 pm one reached 40,000 organic views.",
      },
      {
        title: "Finished output: a creator brief",
        before: "Larkfield Granola had three micro-creators lined up and no brief.",
        after:
          "Create Influencer Brief produced deliverables (one Reel plus three Stories each), key messages, do/don'ts (no “healthiest,” no medical claims), #ad placement per FTC rules, 12-month usage rights and payment terms.",
        result: "No back-and-forth. All three delivered on-brief.",
      },
      {
        title: "Before → After: replying in voice",
        before: "“How do I reply to this mean comment?”",
        after:
          "Write Comment & DM Replies built a playbook with three options each for praise, complaints, questions, trolls and competitor mentions, all in Northpeak Jerky's dry voice.",
        result: "The founder clears 50 comments in 15 minutes, and two team members now sound like one brand.",
      },
    ],
    proTips: [
      "Put the founder on camera once a week. It's the one kind of content AI can't make and the one that converts.",
      "Write captions after you have the image, not before. Upload the photo and say “caption this for pillar X.”",
      "Rerun the calendar monthly with last month's top five posts pasted in. It learns what your audience wants.",
      "Hooks are 80% of a Reel. Spend your AI time there, not on hashtags.",
    ],
    relatedCategories: ["advertising", "photography", "video"],
  },

  // ─────────────────────────────────────────────────────────────── WEBSITE ──
  {
    category: "website",
    title: "AI Website Builder",
    subtitle: "Plan, write and launch a storefront that sounds like you — on Shopify, Wix, Lovable or Framer — in a weekend.",
    whatYouCanDo: {
      intro:
        "AI can plan the sitemap, write every page in your voice, produce FAQs with careful wording, build the SEO content plan and then write the prompt that tells a site builder what to construct. The builders themselves — Shopify Magic, Wix AI, Lovable, Framer AI — turn that prompt into a real site. The result is a launchable store with real copy, not placeholder text.",
      outcomes: [
        "A sitemap with every page's purpose, sections and primary CTA",
        "Section-by-section homepage copy with word counts",
        "Product page copy: title, benefit bullets, ingredient notes, usage and subscription pitch",
        "20 grouped FAQs in your voice with claims guardrails",
        "An About page built from your founder story",
        "An SEO plan: keyword clusters and 12 blog titles with outlines",
        "A paste-ready builder prompt so Wix, Lovable, Shopify or Framer produces an on-brand first draft",
        "A five-email welcome flow for new subscribers",
      ],
      reality:
        "AI copy will cheerfully make health claims the FDA and FTC care about — “supports immunity,” “reduces inflammation” — so strip anything not on your label or substantiated. Payments, shipping rules, tax, subscription apps and accessibility are platform setup work, and a developer is worth hiring once you outgrow the builder's templates.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Plan the sitemap",
        detail: "Run Create Sitemap & Page Plan. Keep it to five to seven pages for launch: home, product, about, FAQ, contact, maybe wholesale. You can add the blog later.",
        taskId: "website-sitemap",
        toolId: "chatgpt",
      },
      {
        step: 2,
        title: "Write every page in one Claude Project",
        detail: "Paste your Brand Profile into a Claude Project, then run Write Homepage Copy, Write Product Page, Write FAQs and Write Founder Story inside it. One project keeps every page in the same voice.",
        taskId: "website-homepage-copy",
        toolId: "claude",
      },
      {
        step: 3,
        title: "Pick the builder and generate the prompt",
        detail: "Shopify if you will sell volume or need a wholesale portal, Wix for a brand site with light commerce, Lovable for a custom landing page, Framer for a design-led site. Run Create Website Builder Prompt for the one you chose.",
        taskId: "website-builder-prompt",
      },
      {
        step: 4,
        title: "Build, then replace every placeholder",
        detail: "Upload your logo, hex codes and product photos to the builder first, paste the prompt, then go page by page pasting your real copy over the generated text.",
        toolId: "shopify",
      },
      {
        step: 5,
        title: "Pre-launch checks and the welcome flow",
        detail: "Ask the AI to flag any claim not on your label. Test checkout with a real card on a phone. Then run Write Welcome Email Series, and schedule the SEO Content Plan for month two.",
        taskId: "website-email-welcome",
      },
    ],
    recommendedTools: [
      {
        toolId: "claude",
        primary: true,
        useFor: "Long-form, on-voice copy across every page without drift.",
        whatToType: "Create a Project, paste your Brand Profile into its instructions, then paste Write Homepage Copy.",
      },
      {
        toolId: "chatgpt",
        useFor: "Sitemap, FAQs, the builder prompt and fast rewrites.",
        whatToType: "Paste Create Sitemap & Page Plan. Then: “Cut this to the five pages I need for launch.”",
      },
      {
        toolId: "shopify",
        useFor: "The store itself for brands that will sell volume, subscriptions and wholesale.",
        whatToType: "Paste the Website Builder Prompt into the theme setup, then paste real copy page by page.",
      },
      {
        toolId: "wix",
        useFor: "Brand and story sites with light e-commerce that a founder can manage alone.",
        whatToType: "Start with Wix AI, paste the builder prompt, upload your logo and photos first.",
      },
      {
        toolId: "lovable",
        useFor: "A custom landing page, waitlist or quiz from a single prompt.",
        whatToType: "Paste the Landing Page copy and the builder prompt together.",
      },
    ],
    featuredTaskIds: [
      "website-homepage-copy",
      "website-product-page",
      "website-builder-prompt",
      "website-faqs",
      "website-founder-story",
    ],
    examples: [
      {
        title: "Before → After: the homepage prompt",
        before: "“Write my homepage.”",
        after:
          "The Prompt Builder version for Tidewater Sparkling Tea asks for hero headline options, three benefit blocks (real brewed tea, 25 calories, four flavors), a how-it's-made section, a social-proof slot, a founder note and a subscription CTA — each with a word count, in a voice described as “coastal, unhurried, plain English.”",
        result:
          "Nine sections, 480 words, pasted into a Shopify theme in 40 minutes. The hero “Tea, sparkling. That's the whole idea.” outperformed the agency-style alternative in a simple A/B.",
      },
      {
        title: "Finished output: a product page",
        before: "Ember Oat Bars' 5-count page still had the theme's template text three weeks after launch.",
        after:
          "Write Product Page produced a title with flavor, five bullets leading with “8 g protein, 5 g sugar,” ingredient notes in plain language, how to eat it, a subscribe-and-save pitch at 15% and a shipping line.",
        result: "The founder reported page conversion moving from about 1.1% to 2.4% in the month after the copy swap.",
      },
      {
        title: "Before → After: the builder prompt",
        before: "“Build me a website.”",
        after:
          "Create Website Builder Prompt wrote a Lovable prompt for a single-product Cinder Cold Brew landing page: sections in order, hex palette, type, the actual headline and bullets, photo slots, one CTA and a footer.",
        result: "Lovable produced a usable page in one pass. The founder swapped in photos and had a waitlist live the same night.",
      },
      {
        title: "Finished output: FAQs with guardrails",
        before: "Wildacre Pet Treats was answering the same eight customer emails every week.",
        after:
          "Write FAQs returned 20 grouped questions. It flagged two it wouldn't answer as asked (“Is it good for allergies?”) and suggested compliant wording plus a consult-your-vet line.",
        result: "Support email volume dropped noticeably once the page went up.",
      },
    ],
    proTips: [
      "Write the copy before you open the builder. Sites built with placeholder copy stay placeholder sites.",
      "Paste your Brand Profile into a Claude Project once; every page will match.",
      "Ask for three hero headlines and pick the one that says what the product is. Clever loses to clear on a homepage.",
      "Before publishing, ask: “Flag any claim on these pages that isn't on my label.”",
    ],
    relatedCategories: ["branding", "photography", "social"],
  },

  // ──────────────────────────────────────────────────────────────── RETAIL ──
  {
    category: "retail",
    title: "AI Retail & Sales Assistant",
    subtitle: "Buyer decks, sell sheets, cold emails and objection prep that read like a seasoned sales director wrote them.",
    whatYouCanDo: {
      intro:
        "Getting into stores is a pitch problem before it's a product problem. AI drafts the buyer deck, sell sheet, retailer-specific pitch and distributor presentation in the structure buyers expect, researches the retailer with citations and rehearses the 20 questions you'll get. It also writes cold emails that sound like a person and follow-ups that don't grovel.",
      outcomes: [
        "Slide-by-slide buyer deck content tailored to one retailer",
        "A one-page sell sheet with the facts table buyers need — case pack, UPC, MSRP, margin",
        "A pitch tailored to a specific retailer's shopper, private-label reality and reset calendar",
        "A distributor deck for UNFI, KeHE or a regional DSD",
        "A cited research brief on the retailer and its category review windows",
        "Three cold-email variants under 150 words plus a four-step follow-up cadence",
        "20 likely buyer questions with model answers and 15 objections with responses",
        "A 12-month promo plan with trade spend as a percentage of sales",
      ],
      reality:
        "AI drafts the deck; it can't tell you your velocity. You must insert real numbers — units per store per week, your actual margin at the retailer's markup, your promo budget — or the deck will contain plausible-looking invented figures, and buyers check. Review windows and buyer names change too; confirm anything research tells you with the vendor portal or a phone call.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Research the retailer",
        detail: "Run Research a Retailer in Perplexity. You want buyer structure, category review timing, vendor requirements and recent moves, with dated sources.",
        taskId: "retail-retailer-research",
        toolId: "perplexity-research",
      },
      {
        step: 2,
        title: "Get your numbers straight first",
        detail: "Before any deck: run Finance AI's Retail Margin Waterfall at this retailer's markup and know your velocity from current doors, farmers markets or DTC. These two numbers are the pitch.",
      },
      {
        step: 3,
        title: "Draft the deck and sell sheet",
        detail: "Run Create Buyer Presentation, replace every bracketed placeholder with your real numbers, then paste it into Gamma for design. Run Create Sell Sheet and lay it out in Canva.",
        taskId: "retail-buyer-presentation",
        toolId: "gamma",
      },
      {
        step: 4,
        title: "Get the meeting",
        detail: "Run Write Cold Email to Buyer. Send one variant, attach the sell sheet, follow the cadence. Name the review window — it's the line buyers notice.",
        taskId: "retail-cold-email",
      },
      {
        step: 5,
        title: "Rehearse, then follow up within 24 hours",
        detail: "Run Prepare for Buyer Meeting and Handle Buyer Objections the day before. Afterward run Write Follow-Up Email Sequence and send the recap the same day.",
        taskId: "retail-meeting-prep",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Decks, sell sheets, cold emails and playing the skeptical buyer.",
        whatToType: "Paste Create Buyer Presentation with the retailer named. Then: “Cut to 8 slides for a 15-minute line review.”",
      },
      {
        toolId: "gamma",
        useFor: "Turning slide text into a designed deck in minutes.",
        whatToType: "New deck → Paste in text → paste the slide content → apply your brand colors.",
      },
      {
        toolId: "perplexity-research",
        useFor: "Retailer research with citations and dates.",
        whatToType: "Paste Research a Retailer. Add: “Date every source.”",
      },
      {
        toolId: "canva",
        useFor: "Sell sheets and line cards.",
        whatToType: "One-page template, your product cut-out, the facts table from the Sell Sheet prompt.",
      },
      {
        toolId: "claude",
        useFor: "Rehearsing objections out loud and writing long retailer-specific narratives.",
        whatToType: "“You are a skeptical category manager. Ask me your five hardest questions one at a time.”",
      },
    ],
    featuredTaskIds: [
      "retail-buyer-presentation",
      "retail-sell-sheet",
      "retail-cold-email",
      "retail-meeting-prep",
      "retail-objections",
    ],
    examples: [
      {
        title: "Before → After: the buyer deck prompt",
        before: "“Make a pitch deck for Whole Foods.”",
        after:
          "The Prompt Builder version for Northpeak Jerky names the region (Pacific Northwest), the category (meat snacks), current doors (14 independents and 2 co-ops), real velocity (4.2 units per store per week in the co-ops), margin at the retailer's standard markup from the waterfall, the promo plan (two TPRs plus demos) and logistics (KeHE).",
        result:
          "A 12-slide deck with a velocity slide built on real co-op scan data. Gamma designed it in ten minutes. The regional buyer asked for samples.",
      },
      {
        title: "Finished output: a sell sheet",
        before: "Ember Oat Bars was attaching a PDF of its website to cold emails.",
        after:
          "Create Sell Sheet produced a headline, three retailer benefits (a sugar gap in the set, 12-month shelf life, demo support), a facts table (12-count case, UPC, $2.99 MSRP, 38% margin, case dimensions) and contact info.",
        result: "One page in Canva. It now goes with every cold email and gets forwarded internally.",
      },
      {
        title: "Before → After: the cold email",
        before: "“Hi, would you like to carry our product?”",
        after:
          "Write Cold Email to Buyer produced three variants under 150 words that lead with a local proof point (“selling 5 per store per week at a nearby independent”), one specific ask (15 minutes before the March meat-snack review) and a one-line credential.",
        result: "Two replies from nine sends. One buyer said the review-window line was why she answered.",
      },
      {
        title: "Finished output: objection prep",
        before: "Tidewater Sparkling Tea's founder was dreading the slotting-fee question.",
        after:
          "Handle Buyer Objections produced 15 objections (“Your velocity is from farmers markets, not grocery”; “We already carry a sparkling tea”) with suggested responses built on her real numbers.",
        result: "The slotting question came up exactly as rehearsed. She had the answer and a counter-offer: free fill plus demos.",
      },
    ],
    proTips: [
      "Never send a deck with AI's placeholder numbers. Search it for “[” and “X%” before you export.",
      "Velocity and margin lead. Story is slide two once you're in the room.",
      "Run Research a Retailer before the cold email. Naming the review window is the line buyers notice.",
      "Ask the AI to play the buyer out loud, one question at a time, and answer without notes.",
    ],
    relatedCategories: ["finance", "investor", "research"],
  },

  // ────────────────────────────────────────────────────────────── INVESTOR ──
  {
    category: "investor",
    title: "AI Investor Assistant",
    subtitle: "A clear deck, a sharp one-pager and rehearsed answers — so the raise is about your numbers, not your slides.",
    whatYouCanDo: {
      intro:
        "AI is very good at the structure of fundraising: the 12–14 slide seed deck, the one-page summary, the forwardable intro blurb, the 25 tough questions. It organizes your story the way investors expect and flags where a real number is missing. It won't decide your valuation or tell you whether the business is fundable.",
      outcomes: [
        "Full slide-by-slide content for a 12–14 slide CPG seed deck",
        "A one-page executive summary",
        "Cold and warm-intro emails plus a forwardable blurb for connectors",
        "25 tough investor questions with model answers in your voice",
        "A use-of-funds plan with milestones and runway logic",
        "An investor-grade competitive analysis with sources",
        "A meeting-prep pack by stage: first call, partner meeting, diligence",
        "A monthly update template that keeps investors warm between rounds",
      ],
      reality:
        "AI can't validate your market size, and it will produce a TAM that sounds great and is wrong — build it bottom-up and cite sources. This is not financial or legal advice: have your attorney review anything you send to investors and your accountant review the financials behind it.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Get the numbers straight",
        detail: "Before the deck, run Finance AI's margin and scenario prompts or Build a Financial Model. You need revenue, gross margin, burn, runway, doors, velocity and — if you sell DTC — CAC and repeat rate.",
      },
      {
        step: 2,
        title: "Draft the deck in Claude",
        detail: "Run Create Pitch Deck Content. Replace every bracketed placeholder with a real number. Then design it in Pitch or Gamma.",
        taskId: "investor-pitch-deck",
        toolId: "claude",
      },
      {
        step: 3,
        title: "Write the one-pager and the email",
        detail: "Run Write Executive Summary, then Write Investor Outreach Email. The forwardable blurb is the piece connectors actually use — make it 90 words.",
        taskId: "investor-executive-summary",
      },
      {
        step: 4,
        title: "Build the competitive analysis with sources",
        detail: "Run Create Competitive Analysis in Perplexity so every claim has a citation. Investors in CPG know the landscape; a wrong fact costs you credibility.",
        taskId: "investor-competitive-analysis",
        toolId: "perplexity-research",
      },
      {
        step: 5,
        title: "Rehearse, then keep them warm",
        detail: "Run Create Investor FAQ and Prepare for Investor Meeting before each call. After any check clears, run Write Monthly Investor Update on the first of every month — including the bad months.",
        taskId: "investor-faq",
      },
    ],
    recommendedTools: [
      {
        toolId: "claude",
        primary: true,
        useFor: "Long, careful deck narrative — and reviewing the whole draft for holes.",
        whatToType: "Paste Create Pitch Deck Content. Then upload the draft: “What would a CPG seed investor push back on?”",
      },
      {
        toolId: "chatgpt",
        useFor: "Outreach emails, the FAQ and fast rewrites.",
        whatToType: "Paste Write Investor Outreach Email. Then: “Make the blurb 90 words.”",
      },
      {
        toolId: "pitch",
        useFor: "A designed deck with view tracking so you know who opened it.",
        whatToType: "New presentation → paste slide text → apply your brand colors → share the tracked link.",
      },
      {
        toolId: "perplexity-research",
        useFor: "Competitive analysis and market data with citations.",
        whatToType: "Paste Create Competitive Analysis. Add: “Cite a dated source for every number.”",
      },
      {
        toolId: "gamma",
        useFor: "A fast designed deck when you don't need tracking.",
        whatToType: "Paste in text → choose a clean theme → export PDF.",
      },
    ],
    featuredTaskIds: [
      "investor-pitch-deck",
      "investor-executive-summary",
      "investor-email",
      "investor-faq",
      "investor-meeting-prep",
    ],
    examples: [
      {
        title: "Before → After: the deck prompt",
        before: "“Write an investor deck for my jam brand.”",
        after:
          "The Prompt Builder version for Bramble & Fig names the stage (pre-seed, raising $400k), traction (62 doors, $210k trailing revenue, 41% gross margin), the insight (reduced-sugar preserves at a premium in a flat category), use of funds (co-packer transition, two hires, trade) and leaves terms to the attorney.",
        result:
          "Thirteen slides. The AI flagged four places that needed real data — TAM, CAC, repeat rate, churn. The founder filled them from Shopify and scan data, then designed it in Pitch.",
      },
      {
        title: "Finished output: the forwardable blurb",
        before: "Halcyon Electrolyte's founder kept asking connectors for intros and sending a six-paragraph email.",
        after:
          "Write Investor Outreach Email produced a 90-word paragraph a connector can paste as-is, with the one metric that matters (46% DTC repeat rate), the raise and a calendar link.",
        result: "Two warm intros were forwarded verbatim.",
      },
      {
        title: "Before → After: the FAQ",
        before: "“What will investors ask?”",
        after:
          "Create Investor FAQ returned 25 questions by category — market, product, unit economics, team, competition, exit — with model answers using Cinder Cold Brew's numbers and a clear marker everywhere a real figure was missing.",
        result: "The founder wrote real answers for seven gaps. The “why won't a big brand just do this” answer was rehearsed and landed.",
      },
      {
        title: "Finished output: the monthly update",
        before: "Larkfield Granola went quiet after the raise and investors started asking if things were okay.",
        after:
          "Write Monthly Investor Update produced a 300-word template: headline metrics, wins, challenges, asks, runway. It goes out on the first of the month.",
        result: "An investor who had passed came back in two updates later.",
      },
    ],
    proTips: [
      "Bottom-up TAM only: doors × velocity × price, by channel. CPG investors know the top-down numbers are fiction.",
      "Ask Claude to review the deck as three investors: a CPG specialist, a generalist seed fund and an angel who runs a grocery chain.",
      "Never let AI write your terms. Valuation, instrument and pro-rata are for your attorney and your own judgment.",
      "Send the update even when it's bad. The template makes that easier.",
    ],
    relatedCategories: ["finance", "research", "retail"],
  },

  // ────────────────────────────────────────────────────────────── RESEARCH ──
  {
    category: "research",
    title: "AI Research Assistant",
    subtitle: "Cited competitor profiles, market sizing, consumer insights and review mining — a week of homework done by Thursday.",
    whatYouCanDo: {
      intro:
        "Deep-research tools read hundreds of sources and return a cited report in about ten minutes: competitor profiles, price-per-ounce tables, trend reports, positioning maps. ChatGPT and Claude turn a pile of customer reviews into ranked themes and quotes you can use in ads. This is the work a category manager would take a week to assemble.",
      outcomes: [
        "Side-by-side competitor profiles: positioning, pricing, pack sizes, distribution, claims",
        "A cited market report: size, growth, segments, channels, tailwinds and headwinds",
        "Evidence-based buyer personas with jobs-to-be-done and purchase barriers",
        "A price-per-unit and price-per-ounce table with your index against the category",
        "Ranked themes and quotable lines mined from hundreds of reviews",
        "A scored opportunity map: whitespace, adjacent formats, underserved needs",
        "A trend report with each trend rated for durability",
        "A positioning map with every competitor placed with evidence, and a ready-to-field survey",
      ],
      reality:
        "AI research is only as good as its sources. It will cite a five-year-old market estimate as current and sometimes gets a competitor's price or distribution wrong. Check every number you'll put in a deck against the source, and buy syndicated data when the decision is expensive. It also can't field the survey — that's you and a panel.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Profile the competitors",
        detail: "Run Research Competitors in Perplexity with four to six names. Ask for a date on every source and a flag on anything older than 18 months.",
        taskId: "research-competitor",
        toolId: "perplexity-research",
      },
      {
        step: 2,
        title: "Size the market and meet the buyer",
        detail: "Run Market Analysis, then Consumer Insights. Save the personas into your Brand Profile's target consumer — every other page gets sharper.",
        taskId: "research-market-analysis",
      },
      {
        step: 3,
        title: "Check pricing in the store",
        detail: "Run Pricing Comparison, then walk one store and verify three shelf prices yourself. Correct the table. Now you trust it.",
        taskId: "research-pricing-comparison",
      },
      {
        step: 4,
        title: "Mine the reviews",
        detail: "Copy 100–300 competitor reviews from Amazon, split into 1-star and 5-star, and run Analyze Customer Reviews. The complaints are your ad copy; the praise is your table stakes.",
        taskId: "research-reviews-analysis",
        toolId: "chatgpt",
      },
      {
        step: 5,
        title: "Map it, and survey only if a real decision hangs on it",
        detail: "Run Build Positioning Map. If you're about to spend real money on the answer, run Build Consumer Survey and field it with a panel.",
        taskId: "research-positioning-map",
      },
    ],
    recommendedTools: [
      {
        toolId: "perplexity-research",
        primary: true,
        useFor: "Cited, current reports on competitors, markets, pricing and trends.",
        whatToType: "Paste Research Competitors. Add: “Date every source and flag anything older than 18 months.”",
      },
      {
        toolId: "gemini-research",
        useFor: "A second opinion and long category-landscape reports that export to Docs.",
        whatToType: "Paste the same Market Analysis prompt; compare where the two reports disagree.",
      },
      {
        toolId: "chatgpt",
        useFor: "Review analysis, survey design and merging several reports into one.",
        whatToType: "Paste 200 reviews, then paste Analyze Customer Reviews.",
      },
      {
        toolId: "claude",
        useFor: "Reading long PDFs you upload and pulling the ten facts that matter.",
        whatToType: "Upload the category report: “List the 10 numbers I'd use in a buyer deck, with page references.”",
      },
      {
        toolId: "notebooklm",
        useFor: "Querying across several reports at once.",
        whatToType: "Upload your reports, then ask: “Where do these disagree on market growth?”",
      },
    ],
    featuredTaskIds: [
      "research-competitor",
      "research-market-analysis",
      "research-reviews-analysis",
      "research-pricing-comparison",
      "research-positioning-map",
    ],
    examples: [
      {
        title: "Before → After: the competitor prompt",
        before: "“Who are my competitors?”",
        after:
          "The Prompt Builder version for Solstice Hot Sauce names five competitors, the channels it sells in, the attributes that matter (heat level, sugar, vinegar base, bottle size, price per ounce, claims, distribution) and asks for citations with dates.",
        result:
          "A nine-column comparison table. Two prices were off by about 50 cents versus the shelf check; the rest held. The row showing no competitor under 5 g sugar at mid-heat became the positioning.",
      },
      {
        title: "Finished output: review mining",
        before: "Copperline Coffee was about to order 10,000 bottles without knowing what buyers hated about the category.",
        after:
          "The founder pasted 240 reviews of two cold-brew concentrates into Analyze Customer Reviews. Ranked themes came back: “too bitter” (31%), “bottle leaks” (18%), “love the ratio” (22%), with 12 quotable lines.",
        result: "“Smooth, not bitter” became headline number one, and the bottle spec was changed before the first run.",
      },
      {
        title: "Before → After: market sizing",
        before: "“How big is the sparkling tea market?”",
        after:
          "For Tidewater: U.S. only, the ready-to-drink tea subsegment, five-year growth, retail versus DTC split, the five largest players' share, two tailwinds, two headwinds, every source dated.",
        result: "A cited report with a bottom-up sanity check. Two numbers made it into the investor deck with footnoted sources.",
      },
      {
        title: "Finished output: a positioning map",
        before: "Wildacre Pet Treats couldn't explain in one slide why it existed.",
        after:
          "Build Positioning Map chose the axes — price per ounce versus ingredient transparency — placed eight competitors with evidence and identified the whitespace.",
        result: "The map became one slide in the buyer deck and the sentence the founder now uses to describe the brand.",
      },
    ],
    proTips: [
      "Ask for dates and URLs on every claim. Then click three of them.",
      "Run the same question in Perplexity and Gemini. Where they disagree, go find out.",
      "Reviews are the cheapest consumer research that exists. Mine competitors' 1-star and 5-star reviews separately.",
      "Save the personas to your Brand Profile. Every other page gets better.",
    ],
    relatedCategories: ["investor", "retail", "advertising"],
  },

  // ─────────────────────────────────────────────────────────────── FINANCE ──
  {
    category: "finance",
    title: "AI Finance & COGS Assistant",
    subtitle: "Margins, price ladders, waterfalls and scenarios — with every formula shown so you can defend the numbers.",
    whatYouCanDo: {
      intro:
        "AI can build the math founders avoid: landed COGS, margin at every channel, the retail waterfall from MSRP down to your net, a price ladder that holds margin across DTC, Amazon, wholesale and distributor, break-even and runway, and base / upside / downside scenarios. It shows the formulas and flags what's off versus CPG benchmarks. Paste it into Google Sheets and it becomes a live model.",
      outcomes: [
        "Landed COGS and gross margin at DTC, wholesale-direct and distributor level, formulas shown",
        "A channel price ladder that hits target margin without channel conflict",
        "A retail margin waterfall including promo, spoils, slotting and freight",
        "Co-packer A vs B vs self-manufacture at each volume break",
        "Normalized supplier quotes with true landed cost per unit",
        "Base, upside and downside 12-month scenarios with cash needs",
        "Break-even units, monthly burn and the month you get there",
        "A 12-month trade-spend plan and a three-year financial model built tab by tab in Sheets",
      ],
      reality:
        "AI does arithmetic well and reads your situation badly — wrong assumptions in, confident nonsense out. Check every input (freight, broker fees, distributor margin, spoils) against real quotes, and have your accountant review anything that goes to a lender, an investor or a tax return. This is not financial advice.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Gather real inputs",
        detail: "Co-packer quote, packaging quotes, freight-in, MSRP, target channels, distributor margin (often 25–30%) and retailer margin (often 35–45% in natural). Real numbers only — no estimates you haven't checked.",
      },
      {
        step: 2,
        title: "Calculate your margins",
        detail: "Run Calculate My Margins with the quote uploaded. Read the benchmark flags. If gross margin at the distributor level is under 30–35%, stop and fix COGS or price before you do anything else.",
        taskId: "finance-margin-calculator",
        toolId: "chatgpt",
      },
      {
        step: 3,
        title: "Build the waterfall and the price ladder",
        detail: "Run Retail Margin Waterfall to see what actually reaches you after promo, spoils and freight, then Build Pricing Model so every channel holds margin without undercutting another.",
        taskId: "finance-margin-waterfall",
      },
      {
        step: 4,
        title: "Move it into Google Sheets",
        detail: "Paste the tables and turn the formula column into live formulas. From now on a new quote means changing one cell. Run Compare COGS Scenarios as quotes come in.",
        taskId: "finance-cogs-comparison",
        toolId: "google-sheets",
      },
      {
        step: 5,
        title: "Plan the year, then review with your accountant",
        detail: "Run Scenario Planning and Break-Even & Cash Runway. Take the output to your accountant before any of it goes to a lender or investor.",
        taskId: "finance-break-even",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "Margin math with formulas shown — and it reads uploaded quotes in PDF or Excel.",
        whatToType: "Upload your co-packer quote, then paste Calculate My Margins.",
      },
      {
        toolId: "google-sheets",
        useFor: "Where the model lives; Gemini in Sheets writes the formulas for you.",
        whatToType: "Paste the margin-stack table, then ask Gemini: “Turn the formula column into live formulas.”",
      },
      {
        toolId: "claude",
        useFor: "Reviewing a full model for logic errors and explaining each line in plain English.",
        whatToType: "Upload the sheet: “Find any assumption that would make a CPG CFO wince.”",
      },
      {
        toolId: "gemini",
        useFor: "Building directly into Sheets if you live in Google Workspace.",
        whatToType: "Paste Build Pricing Model inside Sheets' Gemini panel.",
      },
    ],
    featuredTaskIds: [
      "finance-margin-calculator",
      "finance-margin-waterfall",
      "finance-pricing-model",
      "finance-cogs-comparison",
      "finance-break-even",
    ],
    examples: [
      {
        title: "Before → After: the pricing prompt",
        before: "“What should I charge for my granola?”",
        after:
          "The Prompt Builder version for Larkfield Granola gives a 12 oz bag, landed COGS of $2.38 (co-packer $1.71, packaging $0.42, freight-in $0.25), target channels (natural grocery via distributor, plus DTC), competitor shelf prices of $6.99–$8.99 and a 40% target gross margin at distributor level.",
        result:
          "A ladder of $7.99 MSRP, $4.80 wholesale, $3.65 to distributor. The AI flagged that $3.65 only delivers 35% and offered two fixes — a 10 oz bag or $8.49 MSRP. The founder chose 10 oz.",
      },
      {
        title: "Finished output: a retail waterfall",
        before: "Northpeak Jerky was “selling well” and still losing money every month.",
        after:
          "Retail Margin Waterfall on a $6.49, 3 oz bag: retailer 40%, distributor 27%, promo allowance 8%, spoils 2%, broker 5%, freight-out $0.22. Net to brand $2.18 against $1.62 COGS — $0.56 per unit, 26% contribution.",
        result: "The founder saw the problem in one table and cut the promo allowance plan before the distributor meeting.",
      },
      {
        title: "Before → After: comparing co-packers",
        before: "“Which co-packer is cheaper?”",
        after:
          "Compare COGS Scenarios with two quotes uploaded plus honest self-manufacture inputs (kitchen lease, labor, equipment amortization) at 5k, 20k and 50k units.",
        result: "A won at 5k, B at 20k and up; self-manufacture never won once labor was real. B's $31k MOQ cash became a use-of-funds line.",
      },
      {
        title: "Finished output: break-even and runway",
        before: "Cinder Cold Brew's founder had a feeling about cash and no date.",
        after:
          "Break-Even & Cash Runway with fixed monthly costs of $9,400, $3.10 contribution per unit, 1,900 units a month and 12% monthly growth.",
        result: "Break-even at 3,032 units, reached in month five; seven months of runway. She raised a small bridge two months before the crunch instead of during it.",
      },
    ],
    proTips: [
      "Show your work: ask for the formula in a column next to every number. When a quote changes you update one cell.",
      "Never accept AI's default assumptions for distributor margin, spoils or freight. Type your real ones in.",
      "Build the waterfall before you say yes to a retailer. Margin at the shelf is not margin to you.",
      "Run everything at a 10% COGS increase. If the business breaks, fix it now.",
    ],
    relatedCategories: ["retail", "investor", "operations"],
  },

  // ──────────────────────────────────────────────────────────── OPERATIONS ──
  {
    category: "operations",
    title: "AI Operations Assistant",
    subtitle: "SOPs, production plans, RFQs, QC checklists and compliance lists — the unglamorous documents that keep product on the shelf.",
    whatYouCanDo: {
      intro:
        "Operations is documentation and planning, and AI is good at both. It writes an SOP with roles, steps and records; converts a forecast into a production calendar with lead-time-backed dates; writes an RFQ a co-packer can actually quote; builds inspection checklists; sets reorder points that respect shelf life; and turns a messy call into action items. It also researches co-packers and drafts a regulatory checklist with the rule cited for each item.",
      outcomes: [
        "A complete SOP with purpose, scope, roles, steps, checks and records",
        "A production calendar with run sizes, component orders and dates worked back from lead times",
        "A complete RFQ package that gets you comparable quotes",
        "QC checklists for incoming materials, in-process, finished goods and labeling",
        "Per-SKU safety stock, reorder points and order quantities",
        "A sourced co-packer shortlist with vetting questions and red flags",
        "A category-specific regulatory checklist with the rule cited for every item",
        "Clean meeting summaries with owners and dates, plus a launch task plan",
      ],
      reality:
        "AI can list the FDA, USDA and state rules, but it cannot certify that your label, facility or claims comply — rules change and categories have exceptions. Verify the checklist with a regulatory consultant and have an attorney review supplier contracts; this is not legal advice. Co-packer quality is confirmed by an audit and a plant visit, not a prompt.",
    },
    howToDoIt: [
      {
        step: 1,
        title: "Find and vet co-packers",
        detail: "Run Find & Vet a Co-Packer in Perplexity for a sourced shortlist of five plus the questions to ask. Then visit or video-tour the top two.",
        taskId: "operations-copacker-search",
        toolId: "perplexity-research",
      },
      {
        step: 2,
        title: "Send one RFQ to all of them",
        detail: "Run Create RFQ with your spec sheet uploaded. Send the identical package to every candidate so the quotes line up, then compare them in Finance AI's Compare Supplier Quotes.",
        taskId: "operations-rfq",
        toolId: "chatgpt",
      },
      {
        step: 3,
        title: "Build the compliance checklist before the first print",
        detail: "Run Regulatory & Labeling Checklist, then take it to a regulatory consultant for a one-hour label review. Cheap compared to a relabel.",
        taskId: "operations-compliance-checklist",
      },
      {
        step: 4,
        title: "Plan production and inventory",
        detail: "Run Create Production Plan from your forecast, working every date back from the retailer's reset. Then Plan Inventory in Google Sheets so reorder points stay live.",
        taskId: "operations-production-plan",
        toolId: "google-sheets",
      },
      {
        step: 5,
        title: "Write it down and keep it written down",
        detail: "Run Write an SOP for receiving, QC and fulfillment, Create QC Checklist for each stage, and Summarize Meeting Notes after every supplier call so nothing falls through.",
        taskId: "operations-sop",
      },
    ],
    recommendedTools: [
      {
        toolId: "chatgpt",
        primary: true,
        useFor: "RFQs, SOPs, production plans and meeting summaries — with your spec sheets uploaded.",
        whatToType: "Upload your spec sheet, paste Create RFQ. Then: “Make it a one-page cover email plus a two-page attachment.”",
      },
      {
        toolId: "perplexity-research",
        useFor: "Co-packer search and regulatory lookups with citations and dates.",
        whatToType: "Paste Find & Vet a Co-Packer. Add: “Source every candidate and date every regulation.”",
      },
      {
        toolId: "claude",
        useFor: "Long SOPs, and reading supplier contracts to flag terms to ask your attorney about.",
        whatToType: "Upload the co-packer agreement: “List every clause I should ask a lawyer about and why.”",
      },
      {
        toolId: "google-sheets",
        useFor: "Inventory plans and production calendars that stay live.",
        whatToType: "Paste the Plan Inventory table and let Gemini convert the reorder-point column to formulas.",
      },
      {
        toolId: "notebooklm",
        useFor: "Querying your retailer vendor guides and co-packer specs in one place.",
        whatToType: "Upload the vendor guide PDFs: “What are the pallet and case-label requirements?”",
      },
    ],
    featuredTaskIds: [
      "operations-rfq",
      "operations-copacker-search",
      "operations-compliance-checklist",
      "operations-production-plan",
      "operations-sop",
    ],
    examples: [
      {
        title: "Before → After: the co-packer search",
        before: "“Find me a co-packer for hot sauce.”",
        after:
          "The Prompt Builder version for Solstice Hot Sauce specifies an acidified food in a 5 oz glass woozy, a 10,000-bottle first run growing to 50,000, organic certification wanted, Pacific Northwest or shipping-economical, hot-fill capability, and asks for sources plus vetting questions.",
        result:
          "A shortlist of six with location and known capabilities, and a 15-question vetting list (process authority letter, scheduled process filing, allergen program, lot traceability). Three returned quotes from the RFQ.",
      },
      {
        title: "Finished output: an RFQ that got comparable quotes",
        before: "Ember Oat Bars had three quotes in three formats and no way to compare them.",
        after:
          "Create RFQ produced a product spec, two SKUs, a 25,000-bar first run, flow-wrap plus 5-count carton, SQF preference, target ship date, sample requirements and a required pricing format at 25k, 50k and 100k.",
        result: "Three quotes that lined up column for column. Compare Supplier Quotes ranked them in five minutes.",
      },
      {
        title: "Before → After: the compliance question",
        before: "“Is my label legal?”",
        after:
          "Regulatory & Labeling Checklist for a ready-to-drink tea with caffeine: nutrition facts format, ingredient order, allergen statement, net contents placement, caffeine disclosure, notes on the “natural” claim, state bottle-deposit marks, facility registration — each with the regulation cited and the source date.",
        result: "A regulatory consultant's one-hour review caught two fixes (net-contents placement and a “no sugar added” qualifier) before a 30,000-can print.",
      },
      {
        title: "Finished output: a production plan that hit the reset",
        before: "Mossbrook Kombucha had a Sprouts reset date and a vague sense that labels take a while.",
        after:
          "Create Production Plan took a 4,000-bottle monthly forecast growing 15%, a 14-day ferment, a six-week bottle lead time and a three-week label lead time, and worked every order date backward with two weeks of safety stock.",
        result: "The first order-by date landed nine weeks before the reset. The launch shipped on time; the label order would have been two weeks late without the plan.",
      },
    ],
    proTips: [
      "One RFQ, every co-packer. Quotes you can't compare are quotes you can't use.",
      "Ask for the rule, then ask for the source date. Regulations change; a consultant's hour is cheap insurance.",
      "Work every date backward from the retailer's reset. Lead times are the whole game.",
      "Summarize every supplier call the same day. The action-item list becomes your follow-up email.",
    ],
    relatedCategories: ["finance", "packaging", "retail"],
  },
];

export const TOOLKIT_PAGE_BY_ID = Object.fromEntries(TOOLKIT_PAGES.map((p) => [p.category, p]));
